import { useCallback, useEffect, useState } from "react";

import { toast } from "react-toastify";
import { QRCodeSVG } from "qrcode.react";
import {
	Loader2,
	Smartphone,
	CheckCircle2,
	AlertTriangle,
	KeyRound,
	QrCode,
	Unplug,
	RefreshCw,
} from "lucide-react";

import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { Label } from "../ui/label";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
} from "../ui/dialog";
import api from "../../services/api";
import openSocket from "../../services/socket-io";
import toastError from "../../errors/toastError";
import { recarregarConexoes } from "../../ligacoes/store";

// Aparelho de ligações de uma conexão: um segundo "aparelho conectado" do
// MESMO número, que o motor de ligações usa para fazer e atender chamadas.
// As mensagens continuam na conexão de sempre; nada muda no atendimento.

const AparelhoLigacoesModal = ({ open, onClose, whatsApp }) => {
	const [situacao, setSituacao] = useState(null);
	const [carregando, setCarregando] = useState(false);
	const [codigo, setCodigo] = useState(null);
	const [ocupado, setOcupado] = useState(null);
	const [confirmarRemocao, setConfirmarRemocao] = useState(false);
	const [escaneando, setEscaneando] = useState(false);

	const id = whatsApp?.id;

	const carregar = useCallback(async () => {
		if (!id) return;
		try {
			const { data } = await api.get(`/calls/device/${id}`);
			setSituacao(data);
			if (data.paired) setCodigo(null);
		} catch (err) {
			toastError(err);
		}
	}, [id]);

	useEffect(() => {
		if (!open) {
			setSituacao(null);
			setCodigo(null);
			setConfirmarRemocao(false);
			setEscaneando(false);
			return undefined;
		}
		setCarregando(true);
		carregar().finally(() => setCarregando(false));

		const socket = openSocket();
		socket.on("ligacao-aparelho", (dados) => {
			if (dados.whatsappId === id) carregar();
		});
		return () => socket.disconnect();
	}, [open, id, carregar]);

	const acao = async (chave, fn) => {
		setOcupado(chave);
		try {
			await fn();
		} catch (err) {
			toastError(err);
		}
		setOcupado(null);
	};

	const gerarQr = () =>
		acao("qr", async () => {
			setCodigo(null);
			setEscaneando(true);
			const { data } = await api.post(`/calls/device/${id}`);
			setSituacao(data);
		});

	const pedirCodigo = () =>
		acao("codigo", async () => {
			const { data } = await api.post(`/calls/device/${id}/pair-code`);
			setCodigo(data.code);
		});

	const alternarGravacao = (v) =>
		acao("gravar", async () => {
			const { data } = await api.put(`/calls/device/${id}/recording`, { enabled: v });
			setSituacao(data);
			toast.success(v ? "Ligações passam a ser gravadas." : "Gravação desligada.");
		});

	const remover = () =>
		acao("remover", async () => {
			await api.delete(`/calls/device/${id}`);
			toast.success("Aparelho de ligações desconectado.");
			setConfirmarRemocao(false);
			await carregar();
			recarregarConexoes();
		});

	const pareado = situacao?.paired;
	const numeroPareado = situacao?.jid ? situacao.jid.split("@")[0].split(":")[0].split(".")[0] : "";

	return (
		<Dialog open={open} onOpenChange={(v) => !v && onClose()}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2">
						<Smartphone className="h-5 w-5" />
						Ligações — {whatsApp?.name}
					</DialogTitle>
					<DialogDescription>
						{whatsApp?.channel === "calls"
							? "Linha só de ligações: o celular é pareado direto no motor de ligações, num QR só."
							: "Conecte este número uma segunda vez, como “aparelho conectado”, só para as ligações. As mensagens continuam pela conexão de sempre."}
					</DialogDescription>
				</DialogHeader>

				{carregando || !situacao ? (
					<div className="flex justify-center py-10">
						<Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
					</div>
				) : !situacao.engineRunning ? (
					<div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
						O motor de ligações está parado. Um administrador da plataforma precisa
						ativá-lo em Módulos › Instalados.
					</div>
				) : pareado ? (
					<div className="space-y-3">
						<div className="flex items-center gap-3 rounded-lg border p-3">
							<CheckCircle2 className="h-8 w-8 shrink-0 text-emerald-500" />
							<div className="min-w-0">
								<p className="text-sm font-medium">Aparelho de ligações conectado</p>
								<p className="text-xs text-muted-foreground">
									{numeroPareado ? `Número ${numeroPareado}` : "Pareado"} · já pode fazer
									e receber ligações pelo navegador.
								</p>
							</div>
						</div>

						{situacao.numberMatches === false && (
							<div className="flex gap-2 rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-xs">
								<AlertTriangle className="h-4 w-4 shrink-0 text-destructive" />
								<span>
									O aparelho foi pareado com <b>{numeroPareado}</b>, mas esta conexão é{" "}
									<b>{whatsApp?.number}</b>. As ligações sairiam de um número que o cliente
									não conhece. Desconecte e pareie com o celular desta conexão.
								</span>
							</div>
						)}

						<div className="flex items-center justify-between rounded-lg border p-3">
							<div>
								<Label htmlFor="gravarLigacoes" className="text-sm font-medium">
									Gravar ligações
								</Label>
								<p className="mt-0.5 text-xs text-muted-foreground">
									O áudio entra na conversa como nota interna (o cliente não recebe).
								</p>
							</div>
							<Switch
								id="gravarLigacoes"
								checked={Boolean(situacao.recording)}
								disabled={ocupado === "gravar"}
								onCheckedChange={alternarGravacao}
							/>
						</div>
					</div>
				) : situacao.exists && (escaneando || codigo) ? (
					<div className="space-y-3">
						{codigo ? (
							<div className="rounded-lg border p-4 text-center">
								<p className="text-xs text-muted-foreground">Código de pareamento</p>
								<p className="mt-1 font-mono text-3xl font-semibold tracking-[0.3em]">
									{codigo}
								</p>
								<p className="mt-2 text-xs text-muted-foreground">
									No celular {whatsApp?.number ? `de ${whatsApp.number}` : "deste número"}: WhatsApp › Aparelhos
									conectados › Conectar aparelho › “Conectar com número de telefone”.
								</p>
							</div>
						) : situacao.qr ? (
							<div className="flex flex-col items-center gap-2 rounded-lg border bg-white p-4">
								<QRCodeSVG value={situacao.qr} size={220} />
								<p className="text-center text-xs text-neutral-600">
									No celular {whatsApp?.number ? `de ${whatsApp.number}` : "deste número"}: WhatsApp › Aparelhos
									conectados › Conectar aparelho.
								</p>
							</div>
						) : (
							<div className="flex flex-col items-center gap-2 py-6 text-sm text-muted-foreground">
								<Loader2 className="h-6 w-6 animate-spin" />
								Gerando QR Code…
							</div>
						)}
						<div className="flex flex-wrap justify-center gap-2">
							<Button variant="outline" size="sm" disabled={!!ocupado} onClick={gerarQr}>
								{ocupado === "qr" ? (
									<Loader2 className="h-3.5 w-3.5 animate-spin" />
								) : (
									<RefreshCw className="h-3.5 w-3.5" />
								)}
								Novo QR
							</Button>
							{codigo ? (
								<Button variant="outline" size="sm" onClick={() => setCodigo(null)}>
									<QrCode className="h-3.5 w-3.5" />
									Usar QR Code
								</Button>
							) : (
								<Button
									variant="outline"
									size="sm"
									disabled={!!ocupado || !whatsApp?.number}
									title={!whatsApp?.number ? "A conexão ainda não tem número" : undefined}
									onClick={pedirCodigo}
								>
									{ocupado === "codigo" ? (
										<Loader2 className="h-3.5 w-3.5 animate-spin" />
									) : (
										<KeyRound className="h-3.5 w-3.5" />
									)}
									Parear por código
								</Button>
							)}
						</div>
					</div>
				) : (
					<div className="space-y-3 text-sm">
						<ol className="list-decimal space-y-1 pl-5 text-muted-foreground">
							<li>Gere o QR Code abaixo.</li>
							<li>
								No celular <b>{whatsApp?.number ? `de ${whatsApp.number}` : "deste número"}</b>, abra WhatsApp ›
								Aparelhos conectados › Conectar aparelho.
							</li>
							<li>Leia o QR. Pronto: o botão de ligar aparece nos atendimentos.</li>
						</ol>
						<p className="text-xs text-muted-foreground">
							O WhatsApp aceita até 4 aparelhos conectados por número; este ocupa um.
						</p>
						<Button className="w-full" disabled={!!ocupado} onClick={gerarQr}>
							{ocupado === "qr" ? (
								<Loader2 className="h-4 w-4 animate-spin" />
							) : (
								<QrCode className="h-4 w-4" />
							)}
							Gerar QR Code
						</Button>
					</div>
				)}

				{situacao?.exists && situacao.engineRunning && (
					<DialogFooter className="sm:justify-between">
						{confirmarRemocao ? (
							<div className="flex w-full items-center justify-between gap-2 rounded-lg border border-destructive/40 p-2">
								<span className="text-xs">Desconectar o aparelho de ligações?</span>
								<div className="flex gap-1">
									<Button size="sm" variant="ghost" onClick={() => setConfirmarRemocao(false)}>
										Não
									</Button>
									<Button
										size="sm"
										variant="destructive"
										disabled={ocupado === "remover"}
										onClick={remover}
									>
										{ocupado === "remover" && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
										Desconectar
									</Button>
								</div>
							</div>
						) : (
							<>
								<Button
									variant="ghost"
									className="text-destructive hover:text-destructive"
									onClick={() => setConfirmarRemocao(true)}
								>
									<Unplug className="h-4 w-4" />
									Desconectar aparelho
								</Button>
								<Button onClick={onClose}>Fechar</Button>
							</>
						)}
					</DialogFooter>
				)}
			</DialogContent>
		</Dialog>
	);
};

export default AparelhoLigacoesModal;
